//
// What every long-running job is doing, for as long as the app is open.
//
// One store rather than one subscription per view, for two reasons. A view
// learns the id of its task only after the server answers the request that
// started it, so an event can arrive before that view exists — the store is
// listening from the moment the first progress display mounts. And the recovery
// after a dropped connection (read the snapshot again) belongs in one place,
// not in every open dialog.
//
// A display is fed from two sources: the snapshot read over HTTP and the events
// the socket pushes. They can arrive in either order — a snapshot requested
// after a reconnect is answered while an event that was already on its way is
// still being delivered — so neither may simply overwrite the other. The rules
// for putting them together are here, and they follow from what the server does:
//
//   - An ended task stays ended. services/progress only ever updates a task
//     while it is 'running', so nothing can revive one; a 'running' message
//     arriving after the end is always the older one.
//   - Progress never goes backwards, for the same reason.
//   - A task that runs does not go back to waiting: the queue only ever takes
//     a waiting one up, so a 'queued' after 'running' is the older message.
//
// Nothing here reaches the network or the socket — that is progressWatch, kept
// apart so these rules can be tested without either.
//

export type TaskState = 'queued' | 'running' | 'finished' | 'failed' | 'cancelled'
export type TaskKind = 'import' | 'convert' | 'export'

/** One long-running job, as a progress display needs it. */
export type TaskView = {
    id: string
    projectId: string
    kind: TaskKind | string
    state: TaskState
    /** 0..100 */
    progress: number
    message: string
    currentRow: number
    maxRows: number
    /** What the job produced — the export's download link lives in here. */
    result: Record<string, unknown> | null
    error: string | null
    createdBy: string | null
    createdByName: string | null
    /** Set when somebody other than the one who started it cancelled it. */
    cancelledByName: string | null
    /** What the job is about — a conversion names its converter, data list and file. */
    detail: Record<string, unknown>
    /** When an ended record goes (ISO); null while it runs. */
    expiresAt: string | null
}

export type TaskMap = Readonly<Record<string, TaskView>>

export const isEnded = (task?: TaskView | null) => !!task && task.state !== 'running' && task.state !== 'queued'

const asState = (value: unknown): TaskState => {
    switch (value) {
        case 'queued':
        case 'finished':
        case 'failed':
        case 'cancelled':
            return value
        default:
            // anything else is a job that has not ended
            return 'running'
    }
}

const asNumber = (value: unknown, fallback = 0) => (typeof value === 'number' && isFinite(value) ? value : fallback)

const asText = (value: unknown, fallback = '') => (typeof value === 'string' ? value : fallback)

const asTextOrNull = (value: unknown) => (typeof value === 'string' && value ? value : null)

const asDetail = (value: unknown): Record<string, unknown> =>
    value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : {}

/** The part both shapes name alike. */
const aboutOf = (entry: any) => ({
    createdBy: asTextOrNull(entry.createdBy),
    createdByName: asTextOrNull(entry.createdByName),
    cancelledByName: asTextOrNull(entry.cancelledByName),
    detail: asDetail(entry.detail),
    expiresAt: asTextOrNull(entry.expiresAt),
})

/** A record as GET /tasks and GET /tasks/:taskId present it. */
export function taskFromSnapshot(entry: any): TaskView | null {
    const id = entry?.id ? String(entry.id) : ''
    if (!id) return null

    return {
        id,
        projectId: asText(entry.projectId),
        kind: asText(entry.kind),
        state: asState(entry.state),
        progress: asNumber(entry.progress),
        message: asText(entry.message),
        currentRow: asNumber(entry.currentRow),
        maxRows: asNumber(entry.maxRows),
        result: entry.result ?? null,
        error: entry.error ?? null,
        ...aboutOf(entry),
    }
}

/**
 * A `progress:changed` payload.
 *
 * The event says `jobId` and `status` where the snapshot says `id` and `state`:
 * it predates the register, and the excel export's client read it in that shape
 * long before this store existed. Translated here rather than renamed on the
 * wire.
 */
export function taskFromEvent(payload: any): TaskView | null {
    const id = payload?.jobId ? String(payload.jobId) : ''
    if (!id) return null

    return {
        id,
        projectId: asText(payload.projectId),
        kind: asText(payload.kind),
        state: asState(payload.status),
        progress: asNumber(payload.progress),
        message: asText(payload.message),
        currentRow: asNumber(payload.currentRow),
        maxRows: asNumber(payload.maxRows),
        result: payload.result ?? null,
        error: payload.error ?? null,
        ...aboutOf(payload),
    }
}

/**
 * Whether two readings of a task say the same thing.
 *
 * Compared field by field rather than by identity: the snapshot arrives as a
 * fresh object on every reconnect and usually repeats what is already known.
 */
const sameTask = (a: TaskView, b: TaskView) =>
    a.state === b.state &&
    a.progress === b.progress &&
    a.message === b.message &&
    a.currentRow === b.currentRow &&
    a.maxRows === b.maxRows &&
    a.error === b.error &&
    a.projectId === b.projectId &&
    a.kind === b.kind &&
    a.createdBy === b.createdBy &&
    a.createdByName === b.createdByName &&
    a.cancelledByName === b.cancelledByName &&
    a.expiresAt === b.expiresAt &&
    JSON.stringify(a.result ?? null) === JSON.stringify(b.result ?? null) &&
    JSON.stringify(a.detail) === JSON.stringify(b.detail)

/**
 * Which of the two an observer should believe.
 *
 * Returns `previous` unchanged when the incoming message says nothing new —
 * callers rely on that identity to decide whether anything has to be redrawn.
 */
export function mergeTask(previous: TaskView | undefined, next: TaskView): TaskView {
    if (!previous) return next
    if (sameTask(previous, next)) return previous

    // The end is final, so anything still claiming "running" is stale. And an
    // end reported twice is the same end: an event carries no more truth than
    // the snapshot that already recorded it.
    if (isEnded(previous)) return previous

    // Taken up from the queue, it does not go back to waiting.
    if (previous.state === 'running' && next.state === 'queued') return previous

    // Both still running: a lower value is an older message.
    if (next.progress < previous.progress) return previous

    return next
}

//
// The store itself.
//

let tasks: TaskMap = {}
const listeners = new Set<() => void>()

const publish = () => listeners.forEach((listener) => listener())

const setTasks = (next: TaskMap) => {
    if (next === tasks) return

    tasks = next
    publish()
}

/** Called by useProgress; returns the unsubscribe React expects. */
export function subscribeProgress(listener: () => void): () => void {
    listeners.add(listener)

    return () => {
        listeners.delete(listener)
    }
}

/**
 * The task under this id, or undefined.
 *
 * The identity of the returned object is the signal that something changed — a
 * task nobody touched keeps its object, so a view watching one import does not
 * redraw because another project's export moved.
 */
export const getProgressTask = (taskId: string): TaskView | undefined => (taskId ? tasks[taskId] : undefined)

/** Only for tests: the whole map, to look at without going through an id. */
export const getProgressTasks = (): TaskMap => tasks

/** Merges what GET /tasks answered. */
export function applySnapshot(entries: any[]): void {
    if (!Array.isArray(entries) || !entries.length) return

    let changed = false
    const merged: Record<string, TaskView> = { ...tasks }

    for (const entry of entries) {
        const task = taskFromSnapshot(entry)
        if (!task) continue

        const next = mergeTask(tasks[task.id], task)
        if (next !== tasks[task.id]) {
            merged[task.id] = next
            changed = true
        }
    }

    if (changed) setTasks(merged)
}

/** Merges one `progress:changed` event. */
export function applyEvent(payload: any): void {
    const task = taskFromEvent(payload)
    if (!task) return

    const next = mergeTask(tasks[task.id], task)
    if (next === tasks[task.id]) return

    setTasks({ ...tasks, [task.id]: next })
}

/**
 * Drops everything.
 *
 * Called on sign-out and sign-in (stores/sessionReset): the map is module-level
 * and would otherwise show the previous user their predecessor's file names.
 */
export function clearProgress(): void {
    if (!Object.keys(tasks).length) return

    tasks = {}
    publish()
}
